import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { getSettings, updateSettings } from "../services/settings";
import {
  getUsers,
  updateUser,
  createUser
} from "../services/users";
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";

const Settings = () => {
  const [vessels, setVessels] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [settingsError, setSettingsError] = useState("");
  const [savedMessage, setSavedMessage] = useState("");

const [users, setUsers] = useState([]);
const [editingUser, setEditingUser] = useState(null);
const [savingUser, setSavingUser] = useState(false);
const [deletingUser, setDeletingUser] = useState(false);
const [resetPasswordUser, setResetPasswordUser] = useState(null);
const [newPassword, setNewPassword] = useState("");
const [savingPassword, setSavingPassword] = useState(false);

const [showAddUser, setShowAddUser] = useState(false);
const [savingCreate, setSavingCreate] = useState(false);

const [deletingVessel, setDeletingVessel] = useState(null);
const [confirmingVesselDelete, setConfirmingVesselDelete] = useState(false);

const handleVesselDelete = async () => {
  if (!deletingVessel) return;
  try {
    setConfirmingVesselDelete(true);
    await fetch(`${API_BASE}/vessels/${deletingVessel._id || deletingVessel.id}`, {
      method: "DELETE",
    });
    const vesselRes = await fetch(`${API_BASE}/vessels`);
    const vesselData = await vesselRes.json();
    setVessels(vesselData);
    setDeletingVessel(null);
  } catch (err) {
    console.error("Failed to delete vessel:", err);
  } finally {
    setConfirmingVesselDelete(false);
  }
};

const [newUser, setNewUser] = useState({
  firstname: "",
  lastname: "",
  email: "",
  password: "",
  roles: ["viewer"],
});

const [openSections, setOpenSections] = useState({
  sailing: false,
  booking: false,
  rates: false,
  customer: false,
  vessel: false,
  users: false,
  integrations: false,
});

const handleUserSave = async () => {
  try {
    setSavingUser(true);

    await updateUser(
      editingUser._id,
      {
        roles: editingUser.roles,
        status: editingUser.status,
      }
    );

    const userData = await getUsers();
    setUsers(userData);

    setEditingUser(null);
  } catch (err) {
    console.error(err);
  } finally {
    setSavingUser(false);
  }
};

const handleCreateUser = async () => {
  if (!newUser.firstname || !newUser.lastname || !newUser.email || !newUser.password) {
    alert("Please fill in all fields.");
    return;
  }
  if (newUser.password.length < 6) {
    alert("Password must be at least 6 characters.");
    return;
  }
  try {
    setSavingCreate(true);
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE}/user`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(newUser),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.message || `Server responded ${res.status}`);
    }
    const userData = await getUsers();
    setUsers(userData);
    setShowAddUser(false);
    setNewUser({ firstname: "", lastname: "", email: "", password: "", roles: ["viewer"] });
  } catch (err) {
    console.error(err);
    alert("Failed to create user: " + err.message);
  } finally {
    setSavingCreate(false);
  }
};


const handleDeleteUser = async (targetUser) => {
  const confirmed = window.confirm(
    `Delete ${targetUser.firstname} ${targetUser.lastname}? This cannot be undone.`
  );
  if (!confirmed) return;

  try {
    setDeletingUser(true);
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE}/user/${targetUser._id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Server responded ${res.status}`);
    setUsers((prev) => prev.filter((u) => u._id !== targetUser._id));
  } catch (err) {
    console.error(err);
    alert("Failed to delete user: " + err.message);
  } finally {
    setDeletingUser(false);
  }
};

const handleResetPassword = async () => {
  if (!resetPasswordUser) return;
  try {
    setSavingPassword(true);
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE}/user/${resetPasswordUser._id}/reset-password`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ password: newPassword }),
    });
    if (!res.ok) throw new Error(`Server responded ${res.status}`);
    setResetPasswordUser(null);
    setNewPassword("");
  } catch (err) {
    console.error(err);
    alert("Failed to reset password: " + err.message);
  } finally {
    setSavingPassword(false);
  }
};

  useEffect(() => {
    const loadPageData = async () => {
      try {
        const vesselRes = await fetch(`${API_BASE}/vessels`);
        if (!vesselRes.ok) throw new Error("Failed to load vessels");
        const vesselData = await vesselRes.json();
        setVessels(vesselData);

        const apiSettings = await getSettings();
        setSettings(apiSettings);
const userData = await getUsers();
setUsers(userData);
      } catch (error) {
        console.error(error);
        setSettingsError("Settings API is not loading. Check the API is running.");
      } finally {
        setLoadingSettings(false);
      }
    };

    loadPageData();
  }, []);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateSetting = (section, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: { ...(prev?.[section] || {}), [key]: value },
    }));
  };

  const updateNestedSetting = (section, parentKey, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...(prev?.[section] || {}),
        [parentKey]: {
          ...(prev?.[section]?.[parentKey] || {}),
          [key]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSavedMessage("");
      setSettingsError("");
      const saved = await updateSettings(settings);
      setSettings(saved);
      setSavedMessage("Settings saved.");
      setTimeout(() => setSavedMessage(""), 4000);
    } catch (error) {
      console.error(error);
      setSettingsError("Could not save settings. Check the API is running.");
    }
  };

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.roles?.includes("admin");

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loadingSettings) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-[1600px] px-8 py-8 lg:px-10">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="text-sm text-slate-500">Loading settings...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-[1600px] px-8 py-8 lg:px-10">

        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-950">Settings</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">
              Configure sailing defaults, booking workflow, customer settings, rate settings, and vessel setup.
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              Save Changes
            </button>
          </div>
        </div>

        {/* Flash messages */}
        {settingsError && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {settingsError}
          </div>
        )}
        {savedMessage && (
          <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {savedMessage}
          </div>
        )}

        <div className="space-y-3">

          {/* ── Sailing ── */}
          <AccordionSection
            sectionKey="sailing"
            title="Sailing"
            description="Defaults for sailing creation, status flow, and planning rules."
            isOpen={openSections.sailing}
            onToggle={toggleSection}
          >
            <SettingsGrid>
              <Field label="Default Sailing Status">
                <select
                  className="input"
                  value={settings?.sailing?.defaultStatus || "Draft"}
                  onChange={(e) => updateSetting("sailing", "defaultStatus", e.target.value)}
                >
                  <option value="Draft">Draft</option>
                  <option value="Open">Open</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Closed">Closed</option>
                  <option value="Completed">Completed</option>
                </select>
              </Field>

              <Field label="Default Voyage Duration (days)">
                <input
                  className="input"
                  type="number"
                  min="1"
                  value={settings?.sailing?.defaultVoyageDurationDays || 7}
                  onChange={(e) => updateSetting("sailing", "defaultVoyageDurationDays", Number(e.target.value))}
                />
              </Field>

              <Field label="Weather Buffer (days)">
                <input
                  className="input"
                  type="number"
                  min="0"
                  value={settings?.sailing?.weatherBufferDays || 3}
                  onChange={(e) => updateSetting("sailing", "weatherBufferDays", Number(e.target.value))}
                />
              </Field>

              <Field label="Default Loading Days">
                <input
                  className="input"
                  type="number"
                  min="0"
                  value={settings?.sailing?.defaultLoadingDays || 1}
                  onChange={(e) => updateSetting("sailing", "defaultLoadingDays", Number(e.target.value))}
                />
              </Field>

              <Field label="Default Unloading Days">
                <input
                  className="input"
                  type="number"
                  min="0"
                  value={settings?.sailing?.defaultUnloadingDays || 1}
                  onChange={(e) => updateSetting("sailing", "defaultUnloadingDays", Number(e.target.value))}
                />
              </Field>

              <Field label="Booking Cutoff Before ETD (hours)">
                <input
                  className="input"
                  type="number"
                  min="0"
                  value={settings?.sailing?.bookingCutoffHoursBeforeEtd || 24}
                  onChange={(e) => updateSetting("sailing", "bookingCutoffHoursBeforeEtd", Number(e.target.value))}
                />
              </Field>
            </SettingsGrid>
          </AccordionSection>

          {/* ── Booking ── */}
          <AccordionSection
            sectionKey="booking"
            title="Booking"
            description="Approval flow, allocation behaviour, and capacity rules."
            isOpen={openSections.booking}
            onToggle={toggleSection}
          >
            <SettingsGrid>
              <Field label="Default Booking Status">
                <select
                  className="input"
                  value={settings?.booking?.defaultStatus || "Pending review"}
                  onChange={(e) => updateSetting("booking", "defaultStatus", e.target.value)}
                >
                  <option value="Pending review">Pending review</option>
                  <option value="Draft">Draft</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Confirmed">Confirmed</option>
                </select>
              </Field>

              <Field label="Allocation Method">
                <select
                  className="input"
                  value={settings?.booking?.allocationMethod || "Manual by zone"}
                  onChange={(e) => updateSetting("booking", "allocationMethod", e.target.value)}
                >
                  <option value="Manual by zone">Manual by zone</option>
                  <option value="Suggested by system">Suggested by system</option>
                  <option value="Auto-assign">Auto-assign</option>
                </select>
              </Field>

              <Field label="Over-capacity Handling">
                <select
                  className="input"
                  value={settings?.booking?.overCapacityHandling || "Block booking"}
                  onChange={(e) => updateSetting("booking", "overCapacityHandling", e.target.value)}
                >
                  <option value="Block booking">Block booking</option>
                  <option value="Warn only">Warn only</option>
                </select>
              </Field>

              <Field label="Freight Requires Review">
                <select
                  className="input"
                  value={settings?.booking?.freightRequiresReview ? "true" : "false"}
                  onChange={(e) => updateSetting("booking", "freightRequiresReview", e.target.value === "true")}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </Field>

              <Field label="Livestock Requires Hold Assignment">
                <select
                  className="input"
                  value={settings?.booking?.livestockRequiresHoldAssignment ? "true" : "false"}
                  onChange={(e) => updateSetting("booking", "livestockRequiresHoldAssignment", e.target.value === "true")}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </Field>

              <Field label="Dangerous Goods Allowed">
                <select
                  className="input"
                  value={settings?.booking?.dangerousGoodsAllowed ? "true" : "false"}
                  onChange={(e) => updateSetting("booking", "dangerousGoodsAllowed", e.target.value === "true")}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </Field>

              <Field label="NAIT Tag Scanning">
                <select
                  className="input"
                  value={settings?.booking?.naitScanningEnabled ? "true" : "false"}
                  onChange={(e) => updateSetting("booking", "naitScanningEnabled", e.target.value === "true")}
                >
                  <option value="false">Disabled</option>
                  <option value="true">Enabled</option>
                </select>
                <p className="mt-1.5 text-xs text-slate-400">
                  Shows the handheld NAIT tag scan panel during loading. Leave disabled until scanners are set up.
                </p>
              </Field>
            </SettingsGrid>
          </AccordionSection>

          {/* ── Rates ── */}
          <AccordionSection
            sectionKey="rates"
            title="Rates"
            description="Freight, livestock, route, and accessorial pricing defaults."
            isOpen={openSections.rates}
            onToggle={toggleSection}
          >
            <SettingsGrid>
              <Field label="Default Rate Mode">
                <select
                  className="input"
                  value={settings?.rates?.defaultRateMode || "Manual pricing"}
                  onChange={(e) => updateSetting("rates", "defaultRateMode", e.target.value)}
                >
                  <option value="Manual pricing">Manual pricing</option>
                  <option value="Rate card pricing">Rate card pricing</option>
                  <option value="Customer-specific pricing">Customer-specific pricing</option>
                </select>
              </Field>

              <Field label="Default Freight Unit">
                <select
                  className="input"
                  value={settings?.rates?.defaultFreightUnit || "Per tonne"}
                  onChange={(e) => updateSetting("rates", "defaultFreightUnit", e.target.value)}
                >
                  <option value="Per tonne">Per tonne</option>
                  <option value="Per item">Per item</option>
                  <option value="Per lane">Per lane</option>
                  <option value="Per container">Per container</option>
                </select>
              </Field>

              <Field label="Default Livestock Unit">
                <select
                  className="input"
                  value={settings?.rates?.defaultLivestockUnit || "Per head"}
                  onChange={(e) => updateSetting("rates", "defaultLivestockUnit", e.target.value)}
                >
                  <option value="Per head">Per head</option>
                  <option value="Per kg">Per kg</option>
                  <option value="Per booking">Per booking</option>
                </select>
              </Field>

              <Field label="Route Pricing">
                <select
                  className="input"
                  value={settings?.rates?.routePricingEnabled ? "true" : "false"}
                  onChange={(e) => updateSetting("rates", "routePricingEnabled", e.target.value === "true")}
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </Field>

              <Field label="Accessorial Charges">
                <select
                  className="input"
                  value={settings?.rates?.accessorialChargesEnabled ? "true" : "false"}
                  onChange={(e) => updateSetting("rates", "accessorialChargesEnabled", e.target.value === "true")}
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </Field>

              {settings?.rates?.accessorialChargesEnabled && (
                <>
                  <Field label="Fuel Adjustment Factor (%)">
                    <input
                      type="number"
                      step="0.1"
                      className="input"
                      value={settings?.rates?.fuelAdjustmentFactorPercent ?? 14.6}
                      onChange={(e) => updateSetting("rates", "fuelAdjustmentFactorPercent", Number(e.target.value))}
                    />
                    <p className="mt-1.5 text-xs text-slate-400">
                      Applied as a percentage on top of freight + livestock charges combined.
                    </p>
                  </Field>
                </>
              )}

              <Field label="Default GST Treatment">
                <select
                  className="input"
                  value={settings?.rates?.gstTreatment || "GST exclusive"}
                  onChange={(e) => updateSetting("rates", "gstTreatment", e.target.value)}
                >
                  <option value="GST inclusive">GST inclusive</option>
                  <option value="GST exclusive">GST exclusive</option>
                </select>
              </Field>
            </SettingsGrid>

            <div className="mt-5">
              <Link
                to="/dashboard/settings/rates"
                className="inline-flex rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Open Rate Cards &rarr;
              </Link>
            </div>
          </AccordionSection>

          {/* ── Customer ── */}
          <AccordionSection
            sectionKey="customer"
            title="Customers"
            description="Customer defaults, billing preferences, and booking request behaviour."
            isOpen={openSections.customer}
            onToggle={toggleSection}
          >
            <SettingsGrid>
              <Field label="Customer Approval Required">
                <select
                  className="input"
                  value={settings?.customer?.customerApprovalRequired ? "true" : "false"}
                  onChange={(e) => updateSetting("customer", "customerApprovalRequired", e.target.value === "true")}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </Field>

              <Field label="Default Customer Type">
                <select
                  className="input"
                  value={settings?.customer?.defaultCustomerType || "Mixed"}
                  onChange={(e) => updateSetting("customer", "defaultCustomerType", e.target.value)}
                >
                  <option value="Freight">Freight</option>
                  <option value="Livestock">Livestock</option>
                  <option value="Mixed">Mixed</option>
                </select>
              </Field>

              <Field label="Default Billing Source">
                <select
                  className="input"
                  value={settings?.customer?.defaultBillingSource || "Manual"}
                  onChange={(e) => updateSetting("customer", "defaultBillingSource", e.target.value)}
                >
                  <option value="Xero">Xero</option>
                  <option value="Manual">Manual</option>
                </select>
              </Field>

              <Field label="Booking Request Form">
                <select
                  className="input"
                  value={settings?.customer?.bookingRequestFormEnabled ? "true" : "false"}
                  onChange={(e) => updateSetting("customer", "bookingRequestFormEnabled", e.target.value === "true")}
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </Field>

              <Field label="Confirmation Emails">
                <select
                  className="input"
                  value={settings?.customer?.confirmationEmailsEnabled ? "true" : "false"}
                  onChange={(e) => updateSetting("customer", "confirmationEmailsEnabled", e.target.value === "true")}
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </Field>

              <Field label="Default Currency">
                <select
                  className="input"
                  value={settings?.customer?.defaultCurrency || "NZD"}
                  onChange={(e) => updateSetting("customer", "defaultCurrency", e.target.value)}
                >
                  <option value="NZD">NZD</option>
                </select>
              </Field>
            </SettingsGrid>
          </AccordionSection>

          {/* ── Vessels ── */}
          <AccordionSection
            sectionKey="vessel"
            title="Vessels"
            description="Manage vessels, capacity zones, and operational vessel setup."
            isOpen={openSections.vessel}
            onToggle={toggleSection}
          >
            <div className="space-y-3">
              {vessels.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  No vessels created yet.
                </div>
              ) : (
                vessels.map((vessel) => (
                  <VesselCard
                    key={vessel._id || vessel.id}
                    id={vessel._id || vessel.id}
                    name={vessel.name}
                    code={vessel.code}
                    status={vessel.status || "active"}
                    zones={`${vessel.zones?.length || 0} zones`}
                    capacity={`${vessel.maxWeightKg || "-"} kg / ${vessel.maxLivestockHead || "-"} head`}
                    onDelete={() => setDeletingVessel(vessel)}
                  />
                ))
              )}
            </div>
            <div className="mt-5">
              <Link
                to="/dashboard/settings/vessels/new"
                className="inline-flex rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                + Add Vessel
              </Link>
            </div>
          </AccordionSection>


{/* ── Users ── */}
<AccordionSection
  sectionKey="users"
  title="Users & Permissions"
  description="Roles and access levels across the platform."
  isOpen={openSections.users}
  onToggle={toggleSection}
>
  <div className="overflow-hidden rounded-2xl border border-slate-200">
    <table className="min-w-full divide-y divide-slate-200">
      <thead className="bg-slate-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">
            Name
          </th>

          <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">
            Email
          </th>

          <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">
            Role
          </th>

          <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">
            Status
          </th>

          <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">
            Actions
          </th>
        </tr>
      </thead>

      <tbody className="divide-y divide-slate-200 bg-white">
        {users.map((user) => (
          <tr key={user._id}>
            <td className="px-4 py-3 text-sm text-slate-900">
              {user.firstname} {user.lastname}
            </td>

            <td className="px-4 py-3 text-sm text-slate-600">
              {user.email}
            </td>

            <td className="px-4 py-3">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                {user.roles?.join(", ") || "viewer"}
              </span>
            </td>

            <td className="px-4 py-3">
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  user.status === "active"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {user.status || "active"}
              </span>
            </td>

            <td className="px-4 py-3">
<button
  onClick={() => setEditingUser(user)}
  className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold hover:bg-slate-50"
>
  Edit
</button>
<button
  onClick={() => { setResetPasswordUser(user); setNewPassword(""); }}
  className="ml-2 rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
>
  Reset Password
</button>
<button
  onClick={() => handleDeleteUser(user)}
  disabled={deletingUser}
  className="ml-2 rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
>
  Delete
</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>

<div className="mt-5">
  <button
    onClick={() => setShowAddUser(true)}
    className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
  >
    + Add User
  </button>
</div>

</AccordionSection>

{/* ── Integrations ── */}
<AccordionSection
  sectionKey="integrations"
  title="Integrations"
  description="Platform-wide connections and system defaults."
  isOpen={openSections.integrations}
  onToggle={toggleSection}
>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <div className="space-y-3">
                {(() => {
                  const allIntegrations = [
                    {
                      name: "Xero",
                      enabled: settings?.integrations?.xero?.enabled || false,
                      description: "Customer billing and invoice sync",
                    },
                    {
                      name: "Moneyworks",
                      enabled: settings?.integrations?.moneyworks?.enabled || false,
                      description: "Accounting sync — invoices, customers and rates",
                    },
                    {
                      name: "MarineTraffic",
                      enabled: settings?.integrations?.marineTraffic?.enabled !== false,
                      description: "Tracking links for sailing detail pages",
                    },
                    {
                      name: "Email Notifications",
                      enabled: settings?.integrations?.emailNotifications?.enabled !== false,
                      description: "Booking and status notifications",
                    },
                  ];
                  const activeIntegrations = allIntegrations.filter((i) => i.enabled);

                  if (activeIntegrations.length === 0) {
                    return (
                      <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                        No integrations enabled yet — turn one on using the toggles to the right.
                      </p>
                    );
                  }

                  return activeIntegrations.map((i) => (
                    <IntegrationRow key={i.name} name={i.name} enabled={i.enabled} description={i.description} />
                  ));
                })()}
              </div>

              <SettingsGrid>
                <Field label="Xero Enabled">
                  <select
                    className="input"
                    value={settings?.integrations?.xero?.enabled ? "true" : "false"}
                    onChange={(e) => updateNestedSetting("integrations", "xero", "enabled", e.target.value === "true")}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </Field>

                <Field label="Moneyworks Enabled">
                  <select
                    className="input"
                    value={settings?.integrations?.moneyworks?.enabled ? "true" : "false"}
                    onChange={(e) => updateNestedSetting("integrations", "moneyworks", "enabled", e.target.value === "true")}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </Field>

                {settings?.integrations?.moneyworks?.enabled && (
                  <>
                    <Field label="Datacentre Server URL">
                      <input
                        type="text"
                        className="input"
                        placeholder="https://your-server:6710"
                        value={settings?.integrations?.moneyworks?.serverUrl || ""}
                        onChange={(e) => updateNestedSetting("integrations", "moneyworks", "serverUrl", e.target.value)}
                      />
                    </Field>

                    <Field label="Document Name">
                      <input
                        type="text"
                        className="input"
                        placeholder="e.g. ChathamShipping.moneyworks"
                        value={settings?.integrations?.moneyworks?.documentName || ""}
                        onChange={(e) => updateNestedSetting("integrations", "moneyworks", "documentName", e.target.value)}
                      />
                    </Field>

                    <Field label="Document Username">
                      <input
                        type="text"
                        className="input"
                        value={settings?.integrations?.moneyworks?.username || ""}
                        onChange={(e) => updateNestedSetting("integrations", "moneyworks", "username", e.target.value)}
                      />
                    </Field>

                    <Field label="Document Password">
                      <input
                        type="password"
                        className="input"
                        placeholder="••••••••"
                        value={settings?.integrations?.moneyworks?.password || ""}
                        onChange={(e) => updateNestedSetting("integrations", "moneyworks", "password", e.target.value)}
                      />
                    </Field>

                    <Field label="Requires Folder Login">
                      <select
                        className="input"
                        value={settings?.integrations?.moneyworks?.folderLoginRequired !== false ? "true" : "false"}
                        onChange={(e) => updateNestedSetting("integrations", "moneyworks", "folderLoginRequired", e.target.value === "true")}
                      >
                        <option value="false">No</option>
                        <option value="true">Yes — MoneyWorks Now / folder-secured Datacentre</option>
                      </select>
                    </Field>

                    {settings?.integrations?.moneyworks?.folderLoginRequired !== false && (
                      <>
                        <Field label="Folder Name">
                          <input
                            type="text"
                            className="input"
                            value={settings?.integrations?.moneyworks?.folderName || ""}
                            onChange={(e) => updateNestedSetting("integrations", "moneyworks", "folderName", e.target.value)}
                          />
                        </Field>

                        <Field label="Folder Password">
                          <input
                            type="password"
                            className="input"
                            placeholder="••••••••"
                            value={settings?.integrations?.moneyworks?.folderPassword || ""}
                            onChange={(e) => updateNestedSetting("integrations", "moneyworks", "folderPassword", e.target.value)}
                          />
                        </Field>
                      </>
                    )}

                    <Field label="Default GST/Tax Code">
                      <input
                        type="text"
                        className="input"
                        placeholder="e.g. G"
                        value={settings?.integrations?.moneyworks?.defaultTaxCode || ""}
                        onChange={(e) => updateNestedSetting("integrations", "moneyworks", "defaultTaxCode", e.target.value)}
                      />
                    </Field>

                    <Field label="Auto-create Unknown Customers">
                      <select
                        className="input"
                        value={settings?.integrations?.moneyworks?.autoCreateCustomers ? "true" : "false"}
                        onChange={(e) => updateNestedSetting("integrations", "moneyworks", "autoCreateCustomers", e.target.value === "true")}
                      >
                        <option value="false">No — reject if customer code not found</option>
                        <option value="true">Yes — create a new MoneyWorks customer if needed</option>
                      </select>
                    </Field>

                    <Field label="Post Invoices Immediately">
                      <select
                        className="input"
                        value={settings?.integrations?.moneyworks?.postInvoices ? "true" : "false"}
                        onChange={(e) => updateNestedSetting("integrations", "moneyworks", "postInvoices", e.target.value === "true")}
                      >
                        <option value="false">No — send as Draft (review/edit in Moneyworks before posting)</option>
                        <option value="true">Yes — post immediately (final, appears in reports right away)</option>
                      </select>
                      <p className="mt-1.5 text-xs text-slate-400">
                        Keep this on Draft while testing. Switch to immediate posting once you're confident invoices are coming through correctly.
                      </p>
                    </Field>
                  </>
                )}

                <Field label="MarineTraffic Enabled">
                  <select
                    className="input"
                    value={settings?.integrations?.marineTraffic?.enabled !== false ? "true" : "false"}
                    onChange={(e) => updateNestedSetting("integrations", "marineTraffic", "enabled", e.target.value === "true")}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </Field>

                <Field label="Email Notifications">
                  <select
                    className="input"
                    value={settings?.integrations?.emailNotifications?.enabled !== false ? "true" : "false"}
                    onChange={(e) => updateNestedSetting("integrations", "emailNotifications", "enabled", e.target.value === "true")}
                  >
                    <option value="true">Enabled</option>
                    <option value="false">Disabled</option>
                  </select>
                </Field>

                <Field label="Audit Logging">
                  <select
                    className="input"
                    value={settings?.integrations?.auditLoggingEnabled ? "true" : "false"}
                    onChange={(e) => updateSetting("integrations", "auditLoggingEnabled", e.target.value === "true")}
                  >
                    <option value="true">Enabled</option>
                    <option value="false">Disabled</option>
                  </select>
                </Field>
              </SettingsGrid>
            </div>
             </AccordionSection>

        </div>

{showAddUser && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
      <h3 className="text-lg font-bold">Add User</h3>

      <div className="mt-4">
        <input
          className="input"
          placeholder="First Name"
          value={newUser.firstname}
          onChange={(e) =>
            setNewUser({
              ...newUser,
              firstname: e.target.value,
            })
          }
        />
      </div>

      <div className="mt-4">
        <input
          className="input"
          placeholder="Last Name"
          value={newUser.lastname}
          onChange={(e) =>
            setNewUser({
              ...newUser,
              lastname: e.target.value,
            })
          }
        />
      </div>

      <div className="mt-4">
        <input
          className="input"
          placeholder="Email"
          value={newUser.email}
          onChange={(e) =>
            setNewUser({
              ...newUser,
              email: e.target.value,
            })
          }
        />
      </div>

      <div className="mt-4">
        <input
          className="input"
          type="password"
          placeholder="Password"
          value={newUser.password}
          onChange={(e) =>
            setNewUser({
              ...newUser,
              password: e.target.value,
            })
          }
        />
      </div>

      <div className="mt-4">
        <select
          className="input"
          value={newUser.roles[0]}
          onChange={(e) =>
            setNewUser({ ...newUser, roles: [e.target.value] })
          }
        >
          <option value="viewer">Viewer</option>
          <option value="commercial">Commercial</option>
          <option value="operations">Operations</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={() => setShowAddUser(false)}
          className="rounded-xl border border-slate-300 px-4 py-2"
        >
          Cancel
        </button>

        <button
          onClick={handleCreateUser}
          disabled={savingCreate}
          className="rounded-xl bg-slate-950 px-4 py-2 text-white disabled:opacity-50"
        >
          {savingCreate ? "Creating..." : "Create User"}
        </button>
      </div>
    </div>
  </div>
)}

{editingUser && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
      <h3 className="text-lg font-bold">
        Edit User
      </h3>

      <div className="mt-4">
        <label className="mb-1 block text-sm font-medium">
          Role
        </label>

        <select
          className="input"
          value={editingUser.roles?.[0] || "viewer"}
          onChange={(e) =>
            setEditingUser({
              ...editingUser,
              roles: [e.target.value],
            })
          }
        >
          <option value="admin">Admin</option>
          <option value="operations">Operations</option>
          <option value="commercial">Commercial</option>
          <option value="viewer">Viewer</option>
        </select>
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-sm font-medium">
          Status
        </label>

        <select
          className="input"
          value={editingUser.status || "active"}
          onChange={(e) =>
            setEditingUser({
              ...editingUser,
              status: e.target.value,
            })
          }
        >
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={() => setEditingUser(null)}
          className="rounded-xl border border-slate-300 px-4 py-2"
        >
          Cancel
        </button>

        <button
          onClick={handleUserSave}
          disabled={savingUser}
          className="rounded-xl bg-slate-950 px-4 py-2 text-white"
        >
          {savingUser ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  </div>
)}

{deletingVessel && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
      <h3 className="text-lg font-bold text-slate-950">Delete Vessel?</h3>
      <p className="mt-2 text-sm text-slate-500">
        This will permanently delete <span className="font-semibold text-slate-800">{deletingVessel.name}</span> and all its associated data including zones, capacity settings, and sailing records. This cannot be undone.
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={() => setDeletingVessel(null)}
          disabled={confirmingVesselDelete}
          className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
        >
          Cancel
        </button>
        <button
          onClick={handleVesselDelete}
          disabled={confirmingVesselDelete}
          className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
        >
          {confirmingVesselDelete ? "Deleting..." : "Delete Vessel"}
        </button>
      </div>
    </div>
  </div>
)}

{resetPasswordUser && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
      <h3 className="text-lg font-bold text-slate-950">Reset Password</h3>
      <p className="mt-1 text-sm text-slate-500">
        Set a new password for <span className="font-semibold text-slate-800">{resetPasswordUser.firstname} {resetPasswordUser.lastname}</span>
      </p>
      <div className="mt-4">
        <input
          className="input"
          type="password"
          placeholder="New password (min 6 characters)"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={() => { setResetPasswordUser(null); setNewPassword(""); }}
          disabled={savingPassword}
          className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
        >
          Cancel
        </button>
        <button
          onClick={handleResetPassword}
          disabled={savingPassword || newPassword.length < 6}
          className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {savingPassword ? "Saving..." : "Reset Password"}
        </button>
      </div>
    </div>
  </div>
)}

<style>{`
          .input {
            width: 100%;
            border-radius: 0.9rem;
            border: 1px solid rgb(203 213 225);
            background: white;
            padding: 0.75rem 0.95rem;
            font-size: 0.95rem;
            color: rgb(15 23 42);
            outline: none;
            transition: border-color 0.15s ease, box-shadow 0.15s ease;
          }
          .input::placeholder { color: rgb(148 163 184); }
          .input:hover { border-color: rgb(148 163 184); }
          .input:focus {
            border-color: rgb(15 23 42);
            box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.08);
          }
        `}</style>
      </div>
    </div>
  );
};

const AccordionSection = ({ sectionKey, title, description, children, isOpen, onToggle }) => (
  <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
    <button
      type="button"
      onClick={() => onToggle(sectionKey)}
      className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition hover:bg-slate-50"
    >
      <div>
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        <p className="mt-0.5 text-sm text-slate-500">{description}</p>
      </div>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 text-lg leading-none text-slate-400">
        {isOpen ? "\u2212" : "+"}
      </span>
    </button>
    {isOpen && (
      <div className="border-t border-slate-100 px-6 pb-6 pt-5">{children}</div>
    )}
  </section>
);

const SettingsGrid = ({ children }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">{children}</div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
    {children}
  </div>
);

const VesselCard = ({ id, name, code, status, zones, capacity, onDelete }) => (
  <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
    <div>
      <div className="font-semibold text-slate-900">{name}</div>
      <div className="mt-0.5 text-sm text-slate-500">
        {code} &bull; <span className="capitalize">{status}</span>
      </div>
      <div className="mt-0.5 text-sm text-slate-400">{zones} &bull; {capacity}</div>
    </div>
    <div className="flex shrink-0 items-center gap-2">
      <Link
        to={`/dashboard/settings/vessels/${id}/edit`}
        className="inline-flex rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
      >
        Edit
      </Link>
      <button
        onClick={onDelete}
        className="inline-flex rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 shadow-sm transition hover:bg-red-50"
      >
        Delete
      </button>
    </div>
  </div>
);

const UserRow = ({ name, access }) => (
  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
    <div className="font-semibold text-slate-900">{name}</div>
    <div className="mt-0.5 text-sm text-slate-500">{access}</div>
  </div>
);

const IntegrationRow = ({ name, enabled, description }) => (
  <div className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
    <div>
      <div className="font-semibold text-slate-900">{name}</div>
      <div className="mt-0.5 text-sm text-slate-500">{description}</div>
    </div>
    <span
      className={`mt-0.5 shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        enabled
          ? "bg-emerald-100 text-emerald-700"
          : "bg-slate-100 text-slate-500"
      }`}
    >
      {enabled ? "On" : "Off"}
    </span>
  </div>
);

export default Settings;